import { useCallback, useEffect, useMemo, useState } from 'react'

import { Metadata } from '@redwoodjs/web'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'
import { EmployeeStatus } from 'src/graphql/types/employees'

import AgendaWidget from './components/AgendaWidget'
import ProspectsWidget from './components/ProspectsWidget'
import type { ScopeOption } from './components/ScopeControl'
import ScopeControl from './components/ScopeControl'
import type { DashboardScope } from './lib/dashboardScope'
import {
  defaultScopeForRole,
  isEmployeeListComplete,
  isSameScope,
  pruneScopeToKnownEmployees,
  readStoredScope,
  SCOPABLE_ROLE_TYPES,
  scopeEmployeeIds,
  writeStoredScope,
} from './lib/dashboardScope'

/**
 * Internal preview of the action-items-first dashboard. Reached only from the
 * "New dashboard (beta)" button on the existing dashboard, which is wrapped in
 * `AdminContainer`.
 *
 * The agenda lives in a fixed right-hand column; the rest of the page is a
 * placeholder for the widgets that come next. Company switching is already
 * handled by the sidebar, so there is deliberately no company switcher here.
 *
 * There is one scope control, and every widget obeys it. It replaces both the
 * old dashboard's recruiter multi-select — which defaulted to empty, meaning
 * company-wide, so people saw everyone's data without choosing to — and this
 * page's earlier internal-only "viewing as" picker, which was the same
 * mechanism with a different label on it.
 */
const EMPLOYEE_PAGE_LIMIT = 100

const AgendaDashboardPage = () => {
  const { currentUser } = useAuth()

  const myEmployeeId =
    currentUser?.active?.employeeId ?? currentUser?.employeeId ?? ''
  const fullName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(' ')

  /**
   * Deliberately not `RecruitersContext`, which asks for `roleTypes:
   * ['RECRUITER']` only. `Booking.employeeId` is a plain foreign key to
   * `Employee`, so an admin or ops user can own an appointment — and on a
   * company-wide agenda their rows would appear with nobody able to scope to
   * them or recognise the name.
   */
  const { data: employeesData, loading: employeesLoading } = useQuery(
    GET_EMPLOYEES_ONLY,
    {
      variables: {
        pageInfo: { offset: 0, limit: EMPLOYEE_PAGE_LIMIT },
        filters: { roleTypes: SCOPABLE_ROLE_TYPES },
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  const scopeOptions: ScopeOption[] = useMemo(() => {
    const items = employeesData?.employees?.items ?? []
    return (
      items
        // `EmployeeFiltersInput` has no status filter, so archived employees come
        // back with everyone else. Offering to scope to someone who has left is
        // worse than omitting them.
        .filter(
          (employee) =>
            employee?.employeeId && employee.status === EmployeeStatus.ACTIVE
        )
        .map((employee) => ({
          employeeId: employee.employeeId,
          name:
            [employee.firstName, employee.lastName].filter(Boolean).join(' ') ||
            'Unnamed employee',
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    )
  }, [employeesData])

  // Read once, on the first render: a remembered scope is where you left off,
  // not something that should re-apply while you are working.
  const [scope, setScope] = useState<DashboardScope>(
    () =>
      readStoredScope(myEmployeeId) ??
      defaultScopeForRole(currentUser?.active?.baseRole)
  )

  const handleScopeChange = useCallback(
    (next: DashboardScope) => {
      setScope(next)
      writeStoredScope(myEmployeeId, next)
    },
    [myEmployeeId]
  )

  /**
   * A remembered scope can name people who have since been archived. Their ids
   * would keep narrowing every query while being absent from the picker, so the
   * page would filter on somebody the user can neither see nor untick.
   *
   * Only safe when this page holds *every* employee. `EMPLOYEE_PAGE_LIMIT` caps
   * the query, so on a company with more people than that, anyone past the first
   * page is missing from `scopeOptions` while being perfectly active — pruning
   * against a partial list would strip them from a remembered selection and
   * silently widen the scope. Better to leave a stale id in place than to throw
   * away a real one.
   *
   * Also guarded on the list having arrived: pruning against an empty in-flight
   * result would wipe a good selection on every load.
   */
  useEffect(() => {
    const items = employeesData?.employees?.items
    const totalCount = employeesData?.employees?.totalCount
    if (employeesLoading || !items) return
    if (!isEmployeeListComplete(items.length, totalCount)) return
    const pruned = pruneScopeToKnownEmployees(
      scope,
      scopeOptions.map((option) => option.employeeId)
    )
    if (!isSameScope(pruned, scope)) handleScopeChange(pruned)
  }, [employeesLoading, employeesData, scopeOptions, scope, handleScopeChange])

  const employeeIds = scopeEmployeeIds(scope, myEmployeeId)

  return (
    <>
      <Metadata title="Agenda dashboard" description="Agenda dashboard" />
      <div className="flex h-full flex-col gap-5 px-5 pb-5">
        <div className="flex flex-col justify-between gap-2 text-doubleNickel-brand-500 sm:flex-row sm:items-center">
          <div className="text-lg font-semibold">
            Welcome{fullName ? `, ${fullName}` : ''}!
          </div>

          <div className="flex flex-row items-center gap-2">
            <ScopeControl
              scope={scope}
              onChange={handleScopeChange}
              options={scopeOptions}
              myEmployeeId={myEmployeeId}
            />
            <div className="rounded bg-doubleNickel-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-doubleNickel-gray-600">
              Internal preview
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-row gap-4">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
            {/* Counts sit above the work, scoped by the same control. An empty
                list is company-wide, which is what `scopeEmployeeIds` returns
                null for — the two spellings of "no clause" meet here. */}
            <ProspectsWidget recruiterIds={employeeIds ?? []} />

            <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded border border-dashed border-doubleNickel-gray-300 p-6 text-center">
              <div className="text-sm font-medium text-doubleNickel-gray-600">
                The call list lands here
              </div>
              <div className="mt-1 max-w-md text-xs text-doubleNickel-gray-500">
                This page is an internal preview. Ranking who to call next needs
                new activity-log queries, so it comes after the counts above.
              </div>
            </div>
          </div>

          <div className="flex w-[390px] min-w-0 flex-shrink-0 flex-col">
            {/* Remount on scope change so the widget's collapse state and
                optimistic row state don't carry across from another scope. */}
            <AgendaWidget
              key={employeeIds ? employeeIds.join(',') : 'everyone'}
              employeeIds={employeeIds}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default AgendaDashboardPage
